import {Fragment, type ReactNode, type RefObject, useEffect, useState} from 'react'
import {ActionList, AnchoredOverlay, Label} from '@primer/react'
import {TriangleDownIcon} from '@primer/octicons-react'
import {clsx} from 'clsx'
import type {TaskActionItem} from '../../hooks/use-task-action-items'
import styles from './StickyTaskChatHeaderMenu.module.css'

const EMPTY_ACTION_ITEMS: TaskActionItem[] = []

export interface SessionDetailMetadataProps {
  label: string
  children: ReactNode
  valueClassName?: string
}

export function SessionDetailMetadata({label, children, valueClassName}: SessionDetailMetadataProps) {
  return (
    <div className={styles.sessionDetail}>
      <dt className={styles.sessionDetailLabel}>{label}</dt>
      <dd className={clsx(styles.sessionDetailValue, valueClassName)}>{children}</dd>
    </div>
  )
}

export interface StickyTaskChatHeaderMenuProps {
  title: string
  children: ReactNode
  /** Rows shown to staff only, in their own group below the session details. */
  staffDetails?: ReactNode
  leadingVisual?: ReactNode
  triggerVisual?: ReactNode
  actionItems?: TaskActionItem[]
  actionError?: string
  actionItemAfterRename?: ReactNode
  anchorRef?: RefObject<HTMLElement | null>
  onToggleDetails?: (open: boolean) => void
  className?: string
}

interface StickyTaskChatHeaderMenuContentProps {
  children?: ReactNode
  staffDetails?: ReactNode
  actions?: ReactNode
}

export function StickyTaskChatHeaderMenuContent({
  children,
  staffDetails,
  actions,
}: StickyTaskChatHeaderMenuContentProps) {
  return (
    <div className={styles.overlay} data-testid="environment-details">
      <div className={styles.sessionInfo} hidden={!children}>
        <dl className={styles.sessionDetails} aria-label="Session details">
          {children}
        </dl>
      </div>
      {staffDetails ? (
        <div className={styles.staffInfo} role="group" aria-label="Staff session details">
          <div className={styles.staffHeader}>
            <Label variant="accent">Staff</Label>
          </div>
          <dl className={styles.sessionDetails}>{staffDetails}</dl>
        </div>
      ) : null}
      {actions ? (
        <div className={styles.sessionActions}>
          <ActionList aria-label="Session options">{actions}</ActionList>
        </div>
      ) : null}
    </div>
  )
}

export function StickyTaskChatHeaderMenu({
  title,
  children,
  staffDetails,
  leadingVisual,
  triggerVisual,
  actionItems = EMPTY_ACTION_ITEMS,
  actionError,
  actionItemAfterRename,
  anchorRef,
  onToggleDetails,
  className,
}: StickyTaskChatHeaderMenuProps) {
  const [open, setOpen] = useState(false)

  if (actionError && open) setOpen(false)

  useEffect(() => {
    if (actionError) onToggleDetails?.(false)
  }, [actionError, onToggleDetails])

  function handleOpen() {
    setOpen(true)
    onToggleDetails?.(true)
  }

  function handleClose() {
    setOpen(false)
    onToggleDetails?.(false)
  }

  return (
    <span className={clsx(styles.root, className)} data-testid="sticky-task-chat-header-menu">
      <AnchoredOverlay
        open={open && !actionError}
        anchorRef={anchorRef}
        onOpen={handleOpen}
        onClose={handleClose}
        align="start"
        side="outside-bottom"
        focusTrapSettings={{disabled: true}}
        overlayProps={{role: 'dialog', 'aria-label': `${title} session details`}}
        renderAnchor={anchorProps => (
          <button
            {...anchorProps}
            type="button"
            className={styles.titleButton}
            aria-label={`${title} — session details`}
            aria-haspopup="dialog"
            data-testid="sticky-header-title-menu"
          >
            {leadingVisual}
            <span className={styles.titleText}>{title}</span>
            {triggerVisual ? (
              <span className={styles.environmentVisual} aria-hidden="true">
                {triggerVisual}
              </span>
            ) : null}
            <TriangleDownIcon size={12} className={styles.titleChevron} />
          </button>
        )}
      >
        <StickyTaskChatHeaderMenuContent
          staffDetails={staffDetails}
          actions={
            actionItems.length > 0 || actionItemAfterRename ? (
              <>
                {!actionItems.some(item => item.key === 'rename') && actionItemAfterRename}
                {actionItems.map(item => (
                  <Fragment key={item.key}>
                    {item.href ? (
                      <ActionList.LinkItem
                        href={item.href}
                        onClick={() => {
                          handleClose()
                          item.onSelect()
                        }}
                      >
                        <ActionList.LeadingVisual>
                          <item.icon />
                        </ActionList.LeadingVisual>
                        {item.label}
                      </ActionList.LinkItem>
                    ) : (
                      <ActionList.Item
                        variant={item.variant}
                        disabled={item.disabled}
                        loading={item.loading}
                        onSelect={event => {
                          item.onSelect(event)
                          if (!event.defaultPrevented) handleClose()
                        }}
                      >
                        <ActionList.LeadingVisual>
                          <item.icon />
                        </ActionList.LeadingVisual>
                        {item.label}
                      </ActionList.Item>
                    )}
                    {item.key === 'rename' && actionItemAfterRename}
                  </Fragment>
                ))}
              </>
            ) : undefined
          }
        >
          {children}
        </StickyTaskChatHeaderMenuContent>
      </AnchoredOverlay>
    </span>
  )
}
